import api from "@/http/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

const getOrderChecklist = (order_id: string) => {
  return api.get(`/order-checklist/${order_id}`);
};

export const useOrderChecklist = (order_id?: string) => {
  return useQuery({
    queryKey: ["order-checklist", order_id],
    queryFn: async () => await getOrderChecklist(order_id!),
    enabled: !!order_id,
  });
};

// Minimal shape of the cached axios response, enough to patch an item in place.
type ChecklistCache = {
  data?: { data?: { id: number; checklist?: { id: number; is_checked: boolean }[] }[] };
};

export const useOrderChecklistMutations = (order_id?: string) => {
  const queryClient = useQueryClient();
  const queryKey = ["order-checklist", order_id];
  const invalidate = () => queryClient.invalidateQueries({ queryKey });
  const onError = (message: string) => () => toast.error(message);

  const createTab = useMutation({
    mutationFn: (tab_name: string) => api.post('/order-checklist/tabs', { order_id, tab_name }),
    onSuccess: invalidate,
    onError: onError('Failed to add tab'),
  });

  const renameTab = useMutation({
    mutationFn: ({ id, tab_name }: { id: number; tab_name: string }) =>
      api.patch(`/order-checklist/tabs/${id}`, { tab_name }),
    onSuccess: invalidate,
    onError: onError('Failed to rename tab'),
  });

  const deleteTab = useMutation({
    mutationFn: (id: number) => api.delete(`/order-checklist/tabs/${id}`),
    onSuccess: invalidate,
    onError: onError('Failed to delete tab'),
  });

  const pasteTabs = useMutation({
    mutationFn: (tabs: unknown) => api.post(`/order-checklist/${order_id}/import`, { tabs }),
    onSuccess: () => {
      invalidate();
      toast.success('Checklist pasted');
    },
    onError: onError('Failed to paste checklist'),
  });

  const createItem = useMutation({
    mutationFn: ({ tab_id, title }: { tab_id: number; title: string }) =>
      api.post('/order-checklist/items', { tab_id, title }),
    onSuccess: invalidate,
    onError: onError('Failed to add checklist item'),
  });

  // Checkbox flips immediately; the refetch only confirms it.
  const toggleItem = useMutation({
    mutationFn: ({ id, is_checked }: { id: number; is_checked: boolean }) =>
      api.patch(`/order-checklist/items/${id}`, { is_checked }),
    onMutate: async ({ id, is_checked }: { id: number; is_checked: boolean }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old: ChecklistCache | undefined) => {
        const tabs = old?.data?.data;
        if (!old || !Array.isArray(tabs)) return old;
        return {
          ...old,
          data: {
            ...old.data,
            data: tabs.map((tab) => ({
              ...tab,
              checklist: (tab.checklist ?? []).map((item) =>
                item.id === id ? { ...item, is_checked } : item
              ),
            })),
          },
        };
      });

      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous !== undefined) queryClient.setQueryData(queryKey, context.previous);
      toast.error('Failed to update checklist item');
    },
    onSettled: invalidate,
  });

  const updateItem = useMutation({
    mutationFn: ({ id, title }: { id: number; title: string }) =>
      api.patch(`/order-checklist/items/${id}`, { title }),
    onSuccess: invalidate,
    onError: onError('Failed to update checklist item'),
  });

  // Rows settle into their new order immediately; the refetch only confirms it.
  const reorderItems = useMutation({
    mutationFn: ({ tab_id, item_ids }: { tab_id: number; item_ids: number[] }) =>
      api.patch('/order-checklist/items/reorder', { tab_id, item_ids }),
    onMutate: async ({ tab_id, item_ids }: { tab_id: number; item_ids: number[] }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old: ChecklistCache | undefined) => {
        const tabs = old?.data?.data;
        if (!old || !Array.isArray(tabs)) return old;
        return {
          ...old,
          data: {
            ...old.data,
            data: tabs.map((tab) =>
              tab.id !== tab_id
                ? tab
                : {
                    ...tab,
                    checklist: [...(tab.checklist ?? [])].sort(
                      (a, b) => item_ids.indexOf(a.id) - item_ids.indexOf(b.id)
                    ),
                  }
            ),
          },
        };
      });

      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous !== undefined) queryClient.setQueryData(queryKey, context.previous);
      toast.error('Failed to reorder checklist');
    },
    onSettled: invalidate,
  });

  const deleteItem = useMutation({
    mutationFn: (id: number) => api.delete(`/order-checklist/items/${id}`),
    onSuccess: invalidate,
    onError: onError('Failed to delete checklist item'),
  });

  return { createTab, renameTab, deleteTab, pasteTabs, createItem, toggleItem, updateItem, reorderItems, deleteItem };
};